import { icons } from './icons';
import type { Language } from '$lib/i18n';

export interface Service {
	icon: string;
	title: string;
	description: string;
	gradient: string;
	category: 'know' | 'platform';
}

const servicesData: Record<Language, Service[]> = {
	en: [
		// KNOW - Data & Analytics
		{
			icon: icons.dataIntegration,
			title: 'Data Integration',
			description:
				'OPC-UA, Modbus, MQTT, proprietary protocols, databases. That old PLC nobody wants to touch? We connect it.',
			gradient: 'from-emerald-500/20 to-cyan-500/20',
			category: 'know'
		},
		{
			icon: icons.predictiveMaintenance,
			title: 'Predictive Maintenance',
			description:
				'Health scores, anomaly detection, remaining useful life. Catch failures before they cost you a shift.',
			gradient: 'from-amber-500/20 to-orange-500/20',
			category: 'know'
		},
		{
			icon: icons.oeeProduction,
			title: 'Analysis & Modeling',
			description:
				'OEE, custom KPIs, pattern recognition, whatever metric matters to your operation. We calculate, you act.',
			gradient: 'from-blue-500/20 to-violet-500/20',
			category: 'know'
		},
		// PLATFORM
		{
			icon: icons.apiPlatform,
			title: 'API Platform',
			description:
				'Full REST API with Python and TypeScript SDKs. Your developers build on our data layer.',
			gradient: 'from-indigo-500/20 to-purple-500/20',
			category: 'platform'
		},
		{
			icon: icons.customSoftware,
			title: 'Custom Software',
			description:
				'You describe it, we build it. Dashboards, mobile apps, internal tools, integrations. What you need.',
			gradient: 'from-slate-500/20 to-zinc-500/20',
			category: 'platform'
		}
	],
	no: [
		// KNOW - Data & Analytics
		{
			icon: icons.dataIntegration,
			title: 'Dataintegrasjon',
			description:
				'OPC-UA, Modbus, MQTT, proprietære protokoller, databaser. Den gamle PLS-en ingen vil røre? Vi kobler den til.',
			gradient: 'from-emerald-500/20 to-cyan-500/20',
			category: 'know'
		},
		{
			icon: icons.predictiveMaintenance,
			title: 'Prediktivt vedlikehold',
			description:
				'Helsepoeng, anomalideteksjon, gjenværende levetid. Fang feil før de koster deg et skift.',
			gradient: 'from-amber-500/20 to-orange-500/20',
			category: 'know'
		},
		{
			icon: icons.oeeProduction,
			title: 'Analyse og modellering',
			description:
				'OEE, egendefinerte KPI-er, mønstergjenkjenning, uansett hva som er viktig for din drift. Vi regner, du handler.',
			gradient: 'from-blue-500/20 to-violet-500/20',
			category: 'know'
		},
		// PLATFORM
		{
			icon: icons.apiPlatform,
			title: 'API-plattform',
			description:
				'Full REST API med Python og TypeScript SDK-er. Dine utviklere bygger på vårt datalag.',
			gradient: 'from-indigo-500/20 to-purple-500/20',
			category: 'platform'
		},
		{
			icon: icons.customSoftware,
			title: 'Skreddersydd programvare',
			description:
				'Du beskriver det, vi bygger det. Dashbord, mobilapper, interne verktøy, integrasjoner. Det du trenger.',
			gradient: 'from-slate-500/20 to-zinc-500/20',
			category: 'platform'
		}
	]
};

/** Valid service slugs used in /services/[service] dynamic routes. */
export const VALID_SERVICES = [
	'integration',
	'iot',
	'maintenance',
	'dashboards',
	'custom',
	'ai'
] as const;

/** Returns all services for the given language, ordered by display priority. */
export function getServices(lang: Language): Service[] {
	return servicesData[lang];
}
